<template>
  <div v-if="loading" class="p-8 text-center">
    <Loader />
  </div>
  <ValidationObserver v-else slim v-slot="{ handleSubmit, invalid }" ref="observer">
    <form @submit.prevent="handleSubmit(submit)">
      <BaseLayoutSpaced relaxed>
        <ValidationProvider
          v-for="field in formFieldsWithDefaults"
          :key="field.name"
          :name="field.name"
          :vid="field.name"
          :rules="field.validation"
          v-slot="{ errors, failed }"
          slim
        >
          <BaseFormGroup>
            <BaseFormLabel :for="`${_uid}-${field.name}`">
              {{ field.label }} <template v-if="field.validation.required"> * </template>
            </BaseFormLabel>
            <Component
              v-model="data[field.name]"
              :is="field.component"
              :disabled="submitting"
              v-bind="{
                ...field.options.props,
                ...field.options.attrs,
              }"
              :invalid="failed"
              :id="`${_uid}-${field.name}`"
            />
            <BaseFormInlineMessage v-if="failed" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </ValidationProvider>

        <BaseFormInlineMessage v-if="message" danger>
          {{ message }}
        </BaseFormInlineMessage>

        <div>
          <BaseButton primary type="submit" :disabled="invalid || submitting">
            <span v-if="!submitting">Submit</span>
            <Loader v-else small inverse />
          </BaseButton>
          <BaseButton
            ghost
            type="button"
            :disabled="submitting"
            class="ml-2"
            @click="$emit('cancel')"
          >
            Cancel
          </BaseButton>
        </div>
      </BaseLayoutSpaced>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';
import { HTTP_UNPROCESSABLE_ENTITY, HTTP_OK } from '@/api/http-response-codes';
import { createServiceProviderApi } from '@/api/clients/admin';

const defaultServiceProvider = {
  name: '',
  serviceUuid: null,
  providerUuid: null,
  cost: 0,
};

const defaultField = {
  component: null,
  label: '',
  name: '',
  options: {},
  validation: {},
};

export default {
  name: 'FormServiceProvider',

  mixins: [veeValidateErrorsMixin],

  props: {
    serviceUuid: {
      type: String,
      required: true,
    },
    serviceProviderUuid: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    submitting: false,
    loading: false,
    data: {
      ...defaultServiceProvider,
    },
    providers: [],
    message: null,
  }),

  computed: {
    ...mapState('entities', {
      serviceProvider(state) {
        if (!this.serviceProviderUuid) return null;

        return state.serviceProviders.byId[this.serviceProviderUuid] || null;
      },
    }),

    formFields() {
      return [
        ...(this.data.uuid
          ? []
          : [
              {
                component: 'BaseFormSelect',
                label: 'Provider',
                name: 'providerUuid',
                options: {
                  attrs: {
                    name: 'providerUuid',
                  },
                  props: {
                    options: this.providers,
                  },
                },
                validation: {
                  required: true,
                },
              },
            ]),
        {
          component: 'BaseFormInput',
          label: 'Cost',
          name: 'cost',
          options: {
            attrs: {
              type: 'number',
              name: 'cost',
            },
          },
          validation: {
            required: true,
            min: 0,
          },
        },
      ];
    },

    formFieldsWithDefaults() {
      return this.formFields.map((x) => ({ ...defaultField, ...x }));
    },
  },

  created() {
    if (!this.serviceProviderUuid) {
      this.create();
    } else {
      this.data = { ...defaultServiceProvider, ...(this.serviceProvider || {}) };
    }
  },

  methods: {
    ...mapActions('admin/serviceProviders', ['storeServiceProvider', 'updateServiceProvider']),

    async create() {
      this.loading = true;

      try {
        const { data = {}, status } = await createServiceProviderApi(this.serviceUuid);

        if (status === HTTP_OK) {
          this.providers = data.providers;
          this.data.serviceUuid = this.serviceUuid;
        }
      } catch (e) {
        //
      }

      this.loading = false;
    },

    async submit() {
      if (this.serviceProviderUuid) {
        this.update();
      } else {
        this.store();
      }
    },

    async store() {
      this.submitting = true;

      const submitWithErrorHandling = this.handleFormErrors(this.storeServiceProvider);

      await submitWithErrorHandling({ serviceId: this.serviceUuid, params: this.data });

      this.submitting = false;
    },

    async update() {
      const { serviceProviderUuid } = this;

      this.submitting = true;

      const submitWithErrorHandling = this.handleFormErrors(this.updateServiceProvider);

      await submitWithErrorHandling({ serviceProviderUuid, params: this.data });

      this.submitting = false;
    },

    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.$emit('success');
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
  },
};
</script>
